import moment from 'moment'
import * as Sentry from '@sentry/vue'
import cookie from '@/services/cookie'
import sib from '@/services/sib'
import zoho from '@/services/zoho'
import mixpanelUtils from '@/utils/mixpanel.utils'

import UserApi from '@/api/user'
import ABTestApi from '@/api/abTest'
import AccountApi from '@/api/account'
import utils from '@/util'

import constants from '@/constant'
import PlanAPI from '@/api/plan'
import PlanBenefitAPI from '@/api/planBenefit'
import sentry from '@/services/sentry'
import store from '@/vuex/store'
import EventBus from '@/eventBus'

const { eventCategories, AB_TEST } = constants
let retryGetPlanBenefitTimes = 0

const getUserPlanBenefit = async (currentUser, state) => {
  try {
    const resp = await PlanBenefitAPI.getUserPlanBenefit()
    state.planBenefit = resp
    if (currentUser.plan.indexOf(state.planBenefit.name) === -1 && currentUser.plan.indexOf('edu') === -1) {
      Sentry.captureException(new Error('PlanBenefit mismatch'), {
        extra: {
          planBenefit: state.planBenefit,
          user: {
            plan: currentUser.plan,
            id: currentUser.id,
          },
        },
      })
    }
  } catch (error) {
    if (retryGetPlanBenefitTimes < 10 && error && error.status) {
      retryGetPlanBenefitTimes += 1
      getUserPlanBenefit(currentUser, state)
    }
    Sentry.captureException(new Error('getUserPlanBenefit error'), {
      extra: {
        planBenefit: state.planBenefit,
        user: {
          plan: currentUser.plan,
          id: currentUser.id,
        },
        error,
      },
    })
  }
}

const setCurrentUserAction = async ({ commit, state }, payload) => {
  commit('setCurrentUser', payload)
  try {
    if (!state.planBenefit.name && state.currentUser.id) {
      getUserPlanBenefit(state.currentUser, state)
    }
  } catch (error) {
    // do nothing
  }
}

const setPlan = async ({ commit }, payload) => {
  commit('setPlan', payload)
}

const updateCurrentABTestAction = async ({ commit }, payload) => {
  commit('updateCurrentABTest', payload)
}

const callApiGetABTest = async (abTestCode) => {
  try {
    return ABTestApi.getTest(abTestCode)
  } catch (e) {
    sentry.captureException(e)
    return false
  }
}

const updateUserAfterCreatingTeamAction = async ({ commit, state }) => {
  const { currentUser } = state
  const user = await UserApi.getUser()
  if (user) {
    const patchedUser = {
      ...currentUser,
      ...user,
    }
    commit('updateCurrentUser', patchedUser)
  }
}

const checkUserLanguage = (currentUser, commit) => {
  try {
    const storedLanguageCode = window.localStorage.getItem('ahaslides-presenter-language')
    const presenterLanguage = currentUser.personalInformation && currentUser.personalInformation.presenterLanguage
    const languageCode = presenterLanguage || storedLanguageCode || 'en'
    commit('setPresenterLang', languageCode)
    window.localStorage.setItem('ahaslides-presenter-language', languageCode)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const handleUnleashStart = async (currentUser, state) => {
  await store.dispatch('featureToggles/updateFeatureContext', {
    userId: currentUser.id,
    email: currentUser.email,
    plan: currentUser.plan,
    presenterLanguage: state.presenterLocale,
  })
  store.dispatch('featureToggles/startUnleash')
}

let isFirstTimeLoadService = false

const getCurrentUserAction = async ({
  commit, state, dispatch,
}) => {
  let { currentUser } = state

  try {
    // phải lấy profile 2 lần nếu k thì k thì có bug khi signup từ form ở trang chủ
    // http://localhost:8080/pages/token?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MzU
    // sImlhdCI6MTYzMjIxODAyNiwiZXhwIjoxNjk1MjkwMDI2fQ.dOpl8ICE7i-2624J5pmVQJdO4mcBpUgJkE
    // 7UkvZAlOI&signup=1&name=loi&email=stove+102test@gmail.com
    if (!currentUser.id) {
      currentUser = await UserApi.getUser()
    }
  } catch (error) {
    if (error.status === 401 && state.integrationPlatform === 'GoogleSlide') {
      cookie.deleteCookieGoogleSlide('ahaToken')
      return null
    }
    commit('setIsServerConnectable', false)

    utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'User > Load > Failed')
    throw error
  }

  currentUser.rememberSettings = currentUser.rememberSettings || {}
  if (currentUser.accountId) {
    dispatch('getCurrentTeamAction', currentUser.accountId)
  }
  if (currentUser.id && currentUser.plan !== 'white-label') {
    currentUser.isPlayingDemoEnabled = false
    if (!currentUser.playedPresentationIds ||
      (currentUser.playedPresentationIds && !currentUser.playedPresentationIds.length)) {
      currentUser.isPlayingDemoEnabled = true
    }
  }
  let payload = null
  try {
    payload = await PlanAPI.getMyPlan().then((res) => res.body)
    if (payload.subscription) {
      currentUser.cancel_at_period_end = payload.subscription.cancel_at_period_end
      if (payload.subscription.type) {
        currentUser.paymentSource = payload.subscription.metadata ? payload.subscription.metadata.paymentSource : null
        currentUser.plan = payload.subscription.type
      }
      currentUser.myPlan = payload
    }

    if (payload.subscription && payload.subscription.status === 'trialing') {
      commit('setIsTrialPlan', true)
    }

    if (payload && payload.subscription && payload.subscription.current_period_end) {
      currentUser.current_period_end = payload.subscription.current_period_end
      if ((moment(payload.subscription.current_period_end).isBefore(moment()))) {
        currentUser.plan = 'free'
      }
    }

    setTimeout(() => {
      dispatch('trialPlan/checkToShowTrialModalAction', currentUser)
    }, 1000)
  } catch (e) {
    Sentry.captureException('Error get my plan subscription in current user file', e)
  }
  const TIME_GO_LIVE = 'Mon Nov 13 2023 09:50:34 GMT+0700'
  try {
    if (currentUser) {
      const isUserMigrateToAu = (payload.subscription && payload.subscription.metadata) ?
        !!payload.subscription.metadata.billing_migration_id : false

      currentUser.isBeforeCurrentPeriodStart = currentUser.startDateFirstPlan ?
        moment(TIME_GO_LIVE).isBefore(moment(currentUser.startDateFirstPlan * 1000)) : !isUserMigrateToAu
    } else {
      currentUser.isBeforeCurrentPeriodStart = true
    }
  } catch (e) {
    Sentry.captureException('Error plan before current period start', e)
  }

  commit('setCurrentUser', currentUser)
  commit('setPlan', currentUser.plan)

  // check current user language
  checkUserLanguage(currentUser, commit)

  if (!isFirstTimeLoadService) {
    isFirstTimeLoadService = true
    handleUnleashStart(currentUser, state)
    getUserPlanBenefit(currentUser, state)
  }

  // store user id into for logging purpose
  cookie.setValue('aha-user-id', currentUser.id)
  cookie.setValueByDomain('aha-user-id', currentUser.id)

  sentry.identifyUser(currentUser)
  sib.identifyUser(currentUser)
  zoho.identifyUser(currentUser)

  // TODO: should simplify with one funcion call like above
  mixpanelUtils.identify(currentUser)

  EventBus.$emit('handleAfterLoadedUser', currentUser)

  return currentUser
}

const getCurrentTeamAction = async ({ commit }, teamId) => {
  try {
    const currentAccount = await AccountApi.getAccountById(teamId)
    if (!currentAccount.id) currentAccount.id = 0
    commit('setCurrentAccount', currentAccount)
    mixpanelUtils.setTeamProps(currentAccount)
  } catch (error) {
    Sentry.captureException(error)
    if (error.status === 403) {
      commit('setCurrentAccount', {})
    }
  }
}

const setCurrentUserHasCreatedPresentationAction = async ({ commit, state }) => {
  const userHasCreatedPresentation = state.currentUser.personalInformation &&
        state.currentUser.personalInformation.hasCreatedPresentation

  if (!userHasCreatedPresentation) {
    const res = await UserApi.patchUserById(state.currentUser.id, {
      personalInformation: {
        ...state.currentUser.personalInformation,
        hasCreatedPresentation: true,
      },
    })

    const patchedUser = {
      ...state.currentUser,
      ...res.body,
    }

    commit('setHasOnboardingV3', false)
    commit('setCurrentUser', patchedUser)
  }
}

const updateCurrentUserAction = async ({ commit }, payload) => {
  commit('updateCurrentUser', payload)
}

const patchCurrentUserAction = async ({ commit }, payload) => {
  await UserApi.patchUserById(payload.userId, payload)
  return commit('updateCurrentUser', payload)
}

const setUpABTestPlanUpgradeEditorAction = async ({ commit }) => {
  const resGetABTest = await callApiGetABTest(AB_TEST.PLAN_UPGRADE_EDITOR)

  if (!resGetABTest || !resGetABTest.body) {
    return false
  }

  const { testCode, variant } = resGetABTest.body
  const hasABTestData = testCode && variant

  if (!hasABTestData) {
    return false
  }

  const payload = {}
  payload[AB_TEST.PLAN_UPGRADE_EDITOR] = variant
  commit('updateCurrentABTest', payload)

  return payload
}

const getLimitationPresentationAction = async ({ commit }, presentationId) => {
  const resp = await PlanBenefitAPI.getUserLimitationPresentation(presentationId)
  commit('setLimitationPresentation', resp)
}

export default {
  getCurrentUserAction,
  setCurrentUserAction,
  updateUserAfterCreatingTeamAction,
  setPlan,
  setCurrentUserHasCreatedPresentationAction,
  updateCurrentUserAction,
  patchCurrentUserAction,
  updateCurrentABTestAction,
  getCurrentTeamAction,
  setUpABTestPlanUpgradeEditorAction,
  getLimitationPresentationAction,
}
